
import React, { useState, useEffect, useCallback } from 'react';
import '../styles/sidebar.css';
import type { SelectionContext, GridRow, GridColumn, Attachment, Comment } from '../types/gridTypes';
import { SidebarAttachments } from './SidebarAttachments';
import { SidebarComments } from './SidebarComments';
import { SidebarSummary } from './SidebarSummary';
import { SidebarAI } from './SidebarAI';
import { IconSparkles, IconX, IconAttachment, IconComment, IconSummary } from './icons';

interface RightSidebarProps {
  activeTab: 'attachments' | 'comments' | 'summary' | 'ai' | 'none';
  onClose: () => void;
  onTabChange: (tab: 'attachments' | 'comments' | 'summary' | 'ai') => void;
  selection: SelectionContext;
  allRows: GridRow[];
  allColumns: GridColumn[];
  currentAttachments: Attachment[];
  currentComments: Comment[];
  onAddComment?: (text: string, parentId?: string) => void;
  onUploadAttachments?: (args: { rowId: string; files: FileList }) => Promise<Attachment[]> | Attachment[];
  onDeleteAttachment?: (args: { attachmentId: string; rowId: string }) => Promise<void> | void;
  onEditComment?: (args: { commentId: string; body: string }) => Promise<Comment> | Comment | void;
  onDeleteComment?: (args: { commentId: string }) => Promise<void> | void;
  onResolveComment?: (args: { commentId: string; resolved: boolean }) => Promise<Comment> | Comment | void;
  onAiAction?: (action: any) => void;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({
  activeTab,
  onClose,
  onTabChange,
  selection,
  allRows,
  allColumns,
  currentAttachments,
  currentComments,
  onAddComment,
  onUploadAttachments,
  onDeleteAttachment,
  onEditComment,
  onDeleteComment,
  onResolveComment,
  onAiAction
}) => {
  // Default width 360px
  const [width, setWidth] = useState(360);
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate width based on distance from right edge of viewport
      const newWidth = document.body.clientWidth - e.clientX;
      // Constrain width between 280px and 800px
      if (newWidth >= 280 && newWidth <= 800) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Cursor styling during drag
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (activeTab === 'none') return null;

  const getTitle = () => {
      switch(activeTab) {
          case 'attachments': return 'Attachments';
          case 'comments': return 'Comments';
          case 'summary': return 'Sheet Summary';
          case 'ai': return 'AI Assistant';
          default: return 'Details';
      }
  };

  return (
    <div
        className="sidebar-container border-l shadow-xl flex flex-col h-full shrink-0 z-40 sidebar-enter relative"
        style={{ width }}
    >
      {/* Resize Handle */}
      <div
        className={`sidebar-resize-handle absolute left-0 top-0 bottom-0 w-1.5 -ml-1 cursor-col-resize z-50 transition-colors ${isResizing ? 'resizing' : ''}`}
        onMouseDown={startResizing}
        title="Drag to resize"
      />

      <div className="sidebar-header flex items-center justify-between p-4 border-b backdrop-blur-sm">
         <h3 className="sidebar-title font-bold flex items-center gap-2">
             {activeTab === 'ai' && <IconSparkles className="w-4 h-4 sidebar-ai-icon" />}
             {getTitle()}
         </h3>
         <button onClick={onClose} className="sidebar-close-btn p-1.5 rounded transition-colors">
            <IconX className="w-4 h-4" />
         </button>
      </div>

      <div className="sidebar-tabs flex items-center">
         <div 
            className={`sidebar-tab ${activeTab === 'attachments' ? 'active' : ''}`} 
            onClick={() => onTabChange('attachments')}
            title="Attachments"
         >
             <IconAttachment className="w-5 h-5 mx-auto" />
         </div>
         <div 
            className={`sidebar-tab ${activeTab === 'comments' ? 'active' : ''}`} 
            onClick={() => onTabChange('comments')}
            title="Comments"
         >
             <IconComment className="w-5 h-5 mx-auto" />
         </div>
         <div 
            className={`sidebar-tab ${activeTab === 'summary' ? 'active' : ''}`} 
            onClick={() => onTabChange('summary')}
            title="Summary"
         >
             <IconSummary className="w-5 h-5 mx-auto" />
         </div>
         <div
            className={`sidebar-tab sidebar-tab-ai ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => onTabChange('ai')}
            title="AI Assistant"
         >
             <IconSparkles className="w-5 h-5 mx-auto" />
         </div>
      </div>

      <div className="sidebar-content">
         {activeTab === 'attachments' && (
            <SidebarAttachments 
                attachments={currentAttachments} 
                onUpload={(files) => {
                     if (selection.primaryRowId && onUploadAttachments) {
                         onUploadAttachments({ rowId: selection.primaryRowId, files });
                     }
                }}
                onDelete={(attId) => {
                     if (selection.primaryRowId && onDeleteAttachment) {
                         onDeleteAttachment({ attachmentId: attId, rowId: selection.primaryRowId });
                     }
                }}
            />
         )}
         {activeTab === 'comments' && (
            <SidebarComments 
                comments={currentComments} 
                onAddComment={onAddComment}
                onEditComment={onEditComment}
                onDeleteComment={onDeleteComment}
                onResolveComment={onResolveComment}
            />
         )}
         {activeTab === 'summary' && <SidebarSummary rows={allRows} columns={allColumns} />}
         {activeTab === 'ai' && (
             <SidebarAI 
                rows={allRows}
                columns={allColumns}
                onApplyAction={onAiAction || (() => {})}
             />
         )}
      </div>
    </div>
  );
};
